'use client';

import {
  ArrowRight,
  CalendarDays,
  MapPin,
  Stethoscope,
  User,
} from 'lucide-react';
import { FC, useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Badge } from '@repo/ux/badge';
import { Card, CardContent, CardFooter, CardHeader } from '@repo/ux/card';
import { Skeleton } from '@repo/ux/skeleton';
import { useEventQuery } from '@/graphql';
import { useAnalytics } from '@/hooks/useAnalytics';
import { isValidImageSrc } from '@/lib/utils';
import { Button } from '@/ui/button';
import { FlowErrorState } from '@/ui/flow-error-state';

interface IEventRegistrationCardProps {
  user: string;
  hash: string;
  isAuthenticated?: boolean;
}

export const formatEventDate = (
  value?: string | Date | null,
  timeZone?: string
): string | null => {
  if (!value) {
    return null;
  }
  let date;
  if (value instanceof Date) {
    date = value;
  } else {
    const [yearStr, monthStr, dayStr] = value.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr) - 1;
    const day = Number(dayStr);
    date = new Date(year, month, day);
  }
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return new Intl.DateTimeFormat('en-US', {
    timeZone: timeZone ?? 'UTC',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const EventRegistrationCard: FC<IEventRegistrationCardProps> = ({
  user,
  hash,
  isAuthenticated,
}) => {
  const isTeam = user === 'team';
  const { data, loading, error } = useEventQuery({
    variables: isTeam ? { volunteer_hash: hash } : { participant_hash: hash },
  });
  const {
    trackButton,
    trackPage,
    startFunnel,
    trackFunnelStep,
    setFunnelSession,
    abandonFunnel,
  } = useAnalytics();
  const [proceedClicked, setProceedClicked] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const nextHref = isAuthenticated
    ? `/${isTeam ? 'medical-team' : 'participant'}/register/event/${hash}`
    : `/${isTeam ? 'medical-team' : 'participant'}/signup?hk=${hash}`;

  const roleLabel = isTeam ? 'Medical team member' : 'Participant';
  const ctaLabel = isTeam
    ? isAuthenticated
      ? 'Join as medical team'
      : 'Sign up to join'
    : isAuthenticated
      ? 'Continue to registration'
      : 'Sign up to register';

  // Track funnel start and Step 1
  useEffect(() => {
    if (hash && data?.fetchEvent) {
      startFunnel(hash, data.fetchEvent.name);
      trackFunnelStep('event_landing', 1, hash, {
        event_name: data.fetchEvent.name,
        user_type: user,
        is_authenticated: isAuthenticated,
      });
      setFunnelSession(hash, data.fetchEvent.id);
      trackPage(window.location.pathname, 'Event Registration Landing');
    }
  }, [
    hash,
    data?.fetchEvent,
    user,
    isAuthenticated,
    startFunnel,
    trackFunnelStep,
    setFunnelSession,
    trackPage,
  ]);

  // Track abandonment on page unload without proceeding
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!proceedClicked) {
        abandonFunnel('event_landing', 1, 'page_exit');
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [proceedClicked, abandonFunnel]);

  const handleNextClick = (): void => {
    setProceedClicked(true);
    trackButton('event_registration_next', 'event_registration_card');
    trackFunnelStep('event_landing_complete', 1, hash);
  };

  if (loading) {
    return (
      <Card
        className="w-full md:max-w-148"
        aria-busy="true"
        aria-label="Loading event invitation"
      >
        <CardHeader className="items-center gap-3 text-center">
          <Skeleton className="h-16 w-16 rounded-full" />
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-8 w-64 max-w-full" />
          <Skeleton className="h-4 w-56 max-w-full" />
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-4">
          <Skeleton className="h-6 w-40 rounded-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </CardContent>
        <CardFooter className="flex flex-col items-center gap-3">
          <Skeleton className="h-11 w-full sm:w-52" />
          <Skeleton className="h-4 w-64 max-w-full" />
        </CardFooter>
      </Card>
    );
  }

  if (error || !data?.fetchEvent) {
    return (
      <div className="w-full md:max-w-148">
        <FlowErrorState
          title="This invite link doesn't match an event"
          description="Check that you opened the full link from your invitation email, or contact the event organizer for a new one."
          actionLabel="Go to homepage"
          actionHref="/"
        />
      </div>
    );
  }

  const event = data.fetchEvent;
  const dateLine =
    event.date_display ||
    [
      formatEventDate(event.date_start, event.time_zone ?? undefined),
      formatEventDate(event.date_end, event.time_zone ?? undefined),
    ]
      .filter((value, index, values): value is string =>
        Boolean(value && values.indexOf(value) === index)
      )
      .join(' – ');

  return (
    <Card className="w-full md:max-w-148">
      <CardHeader className="items-center gap-2 text-center justify-center">
        {isValidImageSrc(event.logo) && !logoFailed && (
          <Image
            alt={`${event.name} logo`}
            src={event.logo}
            width={64}
            height={64}
            className="mb-2 h-16 w-auto object-contain justify-self-center"
            onError={() => setLogoFailed(true)}
          />
        )}
        <h1 className="text-heading-4 uppercase">
          Please confirm your selection
        </h1>
        <h1 className="text-heading-5 normal-case">{event.name}</h1>
        {(dateLine || event.state) && (
          <div className="mt-1 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {dateLine && (
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="size-4" aria-hidden="true" />
                {dateLine}
              </span>
            )}
            {event.state && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="size-4" aria-hidden="true" />
                {event.state}
              </span>
            )}
          </div>
        )}
      </CardHeader>

      <CardContent className="flex flex-col items-center gap-4">
        <p className="text-sm leading-6">You are signing up as a</p>
        <Badge variant="secondary" size="lg">
          {isTeam ? (
            <Stethoscope aria-hidden="true" />
          ) : (
            <User aria-hidden="true" />
          )}
          {roleLabel}
        </Badge>
        <div className="text-sm leading-6">for this event.</div>
        <div className="">
          <p className="text-center text-sm leading-6 line-clamp-4">
            Click <span className="font-semibold">{ctaLabel}</span> to continue
            signing up for this event.
          </p>
          <p className="text-center text-sm leading-6 line-clamp-4 mt-4">
            If this is <span className="font-semibold">not correct</span>, close
            this window and check your invitation email for sign-up
            instructions.
          </p>
        </div>
      </CardContent>

      <CardFooter className="flex flex-col items-center gap-3">
        <Button
          asChild
          className="w-full sm:w-auto sm:min-w-52"
          onClickCapture={handleNextClick}
        >
          <Link href={nextHref}>
            {ctaLabel}
            <ArrowRight aria-hidden="true" />
          </Link>
        </Button>
        <p className="text-center text-sm text-muted-foreground">
          Received this link by mistake? You can safely{' '}
          <a href="#" onClick={() => window.close()} className="underline">
            close this window
          </a>{' '}
          .
        </p>
      </CardFooter>
    </Card>
  );
};

export default EventRegistrationCard;
